import React from 'react';
import CodeRain from './CodeRain';
import CyberGrid from './CyberGrid';
import TypingText from './TypingText';
import { usePersona } from '../persona/PersonaContext';

const RetroLandingPage = () => {
  const persona = usePersona();
  const handleDownload = () => {
    window.open(persona.hero.cvFile, '_blank');
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-matrix-bg">
      {/* Cybercore wireframe terrain (synthwave grid scrolling to horizon) */}
      <div className="fixed inset-0 w-full h-full">
        <CyberGrid />
      </div>

      {/* Matrix code rain overlay */}
      <div className="fixed inset-0 w-full h-full">
        <CodeRain columns={32} opacity={0.22} />
      </div>

      {/* Matrix tint + vignette */}
      <div className="absolute inset-0 bg-gradient-to-b from-matrix-bg/80 via-matrix-bg/55 to-matrix-bg/30 pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-t from-matrix-bg via-transparent to-transparent pointer-events-none" />

      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col pointer-events-none">
        {/* Header */}
        <header className="relative z-20 flex items-center justify-between p-6 md:p-8 pointer-events-auto">
          {/* Logo + node id */}
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 md:w-20 md:h-20 relative hover:scale-105 transition-transform"
                 style={{ filter: 'drop-shadow(0 0 8px #00FF66)' }}>
              <svg viewBox="0 0 64 64" fill="none" stroke="#00FF66" strokeWidth="1.5"
                   className="w-full h-full" role="img" aria-label="Tracy Wankio node beacon">
                <title>Tracy Wankio</title>
                <circle cx="32" cy="32" r="28" />
                <circle cx="32" cy="32" r="18" />
                <circle cx="32" cy="32" r="8" fill="#00FF66" fillOpacity="0.2" className="animate-pulse" />
                <circle cx="32" cy="32" r="3" fill="#00FF66" />
                <path d="M32 2 V14 M32 50 V62 M2 32 H14 M50 32 H62" />
              </svg>
            </div>
            <div className="hidden md:block font-mono text-[10px] text-matrix-dim leading-tight tracking-widest uppercase">
              <div>node::tracy-wankio</div>
              <div className="text-matrix-phosphor">status: <span className="animate-pulse">online</span></div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="space-x-6 md:space-x-8 font-mono text-xs md:text-sm">
            {['WORK', 'ABOUT', 'CONTACT'].map((item) => (
              <button
                key={item}
                className="text-matrix-dim hover:text-matrix-phosphor tracking-[0.2em] uppercase border-b border-transparent hover:border-matrix-phosphor transition-all"
              >
                {`> ${item}`}
              </button>
            ))}
          </nav>
        </header>

        {/* HUD reticle decoration top-right */}
        <svg className="absolute top-24 right-8 md:right-16 w-24 h-24 opacity-50 pointer-events-none" viewBox="0 0 80 80" fill="none" stroke="#00FF66" strokeWidth="1">
          <circle cx="40" cy="40" r="28" />
          <circle cx="40" cy="40" r="12" />
          <path d="M40 6 V20 M40 60 V74 M6 40 H20 M60 40 H74" />
          <circle cx="40" cy="40" r="2" fill="#00FF66" />
        </svg>

        {/* Main Content */}
        <main className="relative z-20 flex-1 flex flex-col items-start justify-center px-6 md:px-16 py-12">
          <div className="max-w-5xl space-y-8">
            {/* Terminal eyebrow */}
            <div className="font-mono text-matrix-pale tracking-[0.25em] text-[11px] md:text-sm border-l-2 border-matrix-phosphor pl-3">
              &gt; root@portfolio:~$ ./run --identify
            </div>

            <div className="space-y-3">
              <h1 className="font-anton uppercase text-[10vw] md:text-[6.5vw] leading-[0.92] tracking-[-0.01em] text-matrix-phosphor"
                  style={{ textShadow: '0 0 22px rgba(0,255,102,0.55), 0 0 3px rgba(0,255,102,0.95)' }}>
                <TypingText
                  text={"HELLO, I'M\nTRACY WANKIO"}
                  speed={85}
                  startDelay={400}
                  cursor="_"
                  cursorClassName="ml-1"
                />
              </h1>

              <div className="space-y-5 mt-6">
                <p className="font-mono text-base md:text-xl text-matrix-phosphor tracking-[0.18em] uppercase">
                  &gt; software_developer<span className="animate-pulse">_</span>
                </p>
                <div className="border border-matrix-dim/50 bg-matrix-surface/80 backdrop-blur-sm p-4 max-w-2xl">
                  <div className="font-mono text-[10px] text-matrix-dim uppercase tracking-[0.25em] mb-2">{'/* stack */'}</div>
                  <p className="font-mono text-sm md:text-base text-matrix-pale leading-relaxed">
                    {persona.hero.stackLine}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-4 items-center">
              <button
                onClick={handleDownload}
                className="pointer-events-auto group relative overflow-hidden px-6 py-3 bg-transparent border border-matrix-phosphor text-matrix-phosphor font-mono tracking-[0.25em] text-sm uppercase hover:bg-matrix-phosphor hover:text-matrix-bg transition-all duration-300 shadow-phosphor-sm hover:shadow-phosphor"
              >
                <span>./download_cv ▸</span>
              </button>
              <div className="font-mono text-[10px] text-matrix-dim tracking-widest uppercase">
                {persona.hero.cvLabel}
              </div>
            </div>
          </div>
        </main>

        {/* Bottom status bar */}
        <div className="relative z-20 border-t border-matrix-dim/30 bg-matrix-bg/60 backdrop-blur-sm px-6 md:px-8 py-2 font-mono text-[10px] text-matrix-dim flex justify-between items-center tracking-widest uppercase">
          <span>[ session: secure ] [ proto: matrix.v6 ]</span>
          <span className="text-matrix-phosphor">scroll ↓ for proof_of_work</span>
        </div>
      </div>
    </div>
  );
};

export default RetroLandingPage;
